import { Component, Input, ViewChild, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { DetailsComponent } from '../details/details.component';
import { FormService } from '../service';
import { ParkingComponent } from '../parking/parking.component';
import { PaymentComponent } from '../payment/payment.component';
import { ShuttlebussService } from '../../../../../../services/shuttlebuss.service';
import { ConfirmationStepComponent } from '../confirmation-step/confirmation-step.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { catchError, takeUntil, timeout } from 'rxjs/operators';
import { Subject, throwError, of } from 'rxjs';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MatVerticalStepperScrollerDirective } from '../../../../../../../directives/mat-stepper-vertical-scroller.directive';
import { Router } from '@angular/router';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { StepAdditionalServicesComponent } from '../step-additional-services/step-additional-services.component';
import { StepperService } from '../../../../../../services/stepper.service';
import { BookingConfigService } from '../../../../../../services/booking-config.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CartService } from '../../../../../../services/cart.service';
import { MaintenanceService } from '../../../../../../../services/maintenance.service';
import { CheckoutServices } from '../../../../../../../../app/parkifly-bookings/services/checkout.service';

/**
 * @title Stepper overview
 */
@Component({
  standalone: true,
  selector: 'app-stepper',
  templateUrl: './stepper.component.html',
  styleUrl: './stepper.component.scss',
  imports: [
    MatButtonModule,
    MatStepperModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    ParkingComponent,
    TranslocoModule,
    MatVerticalStepperScrollerDirective,
    DetailsComponent,
    PaymentComponent,
    ConfirmationStepComponent,
    StepAdditionalServicesComponent
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    'style': 'font-family: Inter, sans-serif;'
  }
})
export class StepperComponent implements OnDestroy, AfterViewInit {
  @ViewChild('stepper') stepper!: MatStepper;
  @ViewChild(PaymentComponent) paymentComponent!: PaymentComponent;
  @ViewChild(DetailsComponent) detailsComponent!: DetailsComponent;
  form: any = null;
  detailsGroup: any;
  isLinear = true;
  orderId: string = null;
  isParkingSelected = false;
  bookingButtonValue: string = "";
  subscription: any;
  destroyed = new Subject<void>();
  currentScreenSize!: string;
  displayNameMap = new Map([
    [Breakpoints.XSmall, 'XSmall'],
    [Breakpoints.Small, 'Small'],
    [Breakpoints.Medium, 'Medium'],
    [Breakpoints.Large, 'Large'],
    [Breakpoints.XLarge, 'XLarge'],
  ]);
  isLoading = false;
  isadditionalServicesEnabled: boolean = false;
  isConfirmationStepActive: boolean = false;
  paymentGroup = new FormGroup({
    goToNextStep: new FormControl(null, Validators.required)
  });
  confirmGroup = new FormGroup({
    goToNextStep: new FormControl(null, Validators.required)
  });
  email: string = '';
  bookingNumber: any = '';
  isPaymentStepActive: boolean = true;
  lastStepIndex: number = 0;
  site: string = '';

  constructor(
    private _form: FormService,
    private _shuttleBussService: ShuttlebussService,
    private breakpointObserver: BreakpointObserver,
    private translocoService: TranslocoService,
    private _router: Router,
    private _stepperService: StepperService,
    private bookingConfigService: BookingConfigService,
    private snackBar: MatSnackBar,
    private cartService: CartService,
    private checkoutService: CheckoutServices,
    private maintenanceService: MaintenanceService
  ) {

    const config = this.bookingConfigService.getConfig();
    this.site = config.site;

    this._form.getForm().get('parking').controls.selectedParking.valueChanges.subscribe((parking: any) => {
      if (parking) {
        this.isadditionalServicesEnabled = parking.additionalServicesEnabled ?? false;
      } else {
        this.isadditionalServicesEnabled = false;
      }
    });

    // this.isadditionalServicesEnabled = config?.additionalServices;
    this.detailsGroup = _form.getForm().get('details');
    this.subscription = this.translocoService.langChanges$.subscribe((lang) => {
      this.bookingButtonValue = this.translocoService.translate('SUMMARY.TO_PAYMENT');
    });
    this.breakpointObserver
      .observe([
        Breakpoints.XSmall,
        Breakpoints.Small,
        Breakpoints.Medium,
        Breakpoints.Large,
        Breakpoints.XLarge,
      ])
      .pipe(takeUntil(this.destroyed))
      .subscribe(result => {
        for (const query of Object.keys(result.breakpoints)) {
          if (result.breakpoints[query]) {
            this.currentScreenSize = this.displayNameMap.get(query) ?? 'Unknown';
          }
        }
      });
  }

  ngAfterViewInit() {
    if (this.stepper) {
      this._stepperService.setStepper(this.stepper);
    }
    if (this._form?.selectedParking) {
      this.isParkingSelected = true;
    }
    this.maintenanceService.checkMaintenanceTime();
    this.subscribeToPaymentViewChanges();
    this.subscribeToDetailsViewChanges();
  }

  private subscribeToPaymentViewChanges() {
    // Use MutationObserver to specifically watch for the payment view div
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          const targetDiv = document.getElementById('swedbank-pay-seamless-view-page');
          if (targetDiv && targetDiv.children.length > 0) {
            // Add delay to ensure payment content is fully rendered
            setTimeout(() => this.scrollToPaymentView(), 500);
            observer.disconnect(); // Stop observing once found
          }
        }
      });
    });

    // Start observing the document for the specific payment div
    setTimeout(() => {
      observer.observe(document.body, { childList: true, subtree: true });
    }, 100);
  }

  private scrollToPaymentView() {
    const targetDiv = document.getElementById('swedbank-pay-seamless-view-page');
    if (targetDiv && targetDiv.children.length > 0) {
      targetDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  private subscribeToDetailsViewChanges() {
    if (this.stepper) {
      this.stepper.selectionChange.subscribe((event: StepperSelectionEvent) => {
        const stepLabel = event.selectedStep.label?.toLowerCase();
        if (stepLabel && (stepLabel.includes('your details') || stepLabel.includes('dina uppgifter'))) {
          setTimeout(() => {
            const detailsHeading = document.querySelector('.DinaUppgifter');
            if (detailsHeading) {
              detailsHeading.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
              window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            }
          }, 200);
        }
      });
    }
  }

  ngOnInit(): void {
    this.maintenanceService.checkMaintenanceTime();
    this.updatePaymentStepControlValidity();
    this.bookingButtonValue = this.translocoService.translate('SUMMARY.TO_PAYMENT');
    this._form._toThePayment.pipe(takeUntil(this.destroyed)).subscribe((change: any) => {
      if (change) {
        this.onBookClick();
      }
    });
    this._form._isPaymentCompleted.pipe(takeUntil(this.destroyed)).subscribe((change: any) => {
      if (change == true) {
        this.updateConfirmStepControlValidity();
        this.getOrder();
      }
    });
  }

  getOrder() {
    this.checkoutService.getOrder(this.orderId).subscribe(order => {
      const orderNr = order.orderNr;
      const email = order.customer.email;
      if (orderNr != null && email != null) {
        this._router.navigate(['/parkifly-new/booking-overview', { email: email, booking_number: orderNr }]);
      }
    });
  }

  updatePaymentStepControlValidity() {
    const control = this.paymentGroup.get('goToNextStep');
    if (!this._form.paymentReference) {
      control?.setValue(null);
      control?.setErrors({ required: true });
    } else {
      control?.setErrors(null);
      control?.setValue(true);
    }
    control?.markAsTouched();
    control?.markAsDirty();
    control?.updateValueAndValidity();
    this.paymentGroup.updateValueAndValidity();
  }

  updateConfirmStepControlValidity() {
    const control = this.confirmGroup.get('goToNextStep');
    if (!this._form.isPaymentCompleted) {
      control?.setValue(null);
      control?.setErrors({ required: true });
    } else {
      control?.setErrors(null);
      control?.setValue(true);
    }
    control?.markAsTouched();
    control?.markAsDirty();
    control?.updateValueAndValidity();
    this.confirmGroup.updateValueAndValidity();
  }

  onBookClick() {
    const isSubscription = sessionStorage.getItem('showSubscription') === 'true';
    const isParkingPermitUser = sessionStorage.getItem('isParkingPermitUser') === 'true';
    const isQualityBooking = this.site?.toLowerCase() == 'quality-xpo-arlanda' || this.site?.toLowerCase() == 'quality';

    if (isSubscription) {
      const personsControl = this.detailsGroup.controls.persons;
      const parkingspacesizeControl = this.detailsGroup.controls.parkingspacesize;
      personsControl.clearValidators();
      parkingspacesizeControl.clearValidators();
      personsControl.updateValueAndValidity();
      parkingspacesizeControl.updateValueAndValidity();
    }

    if (isParkingPermitUser) {
      Object.keys(this.detailsGroup.controls).forEach(key => {
        this.detailsGroup.controls[key].clearValidators();
        this.detailsGroup.controls[key].updateValueAndValidity();
      });
    }

    if (isQualityBooking) {
      const bookingNrControl = this.detailsGroup.controls.bookingNumber;
      bookingNrControl.clearValidators();
      bookingNrControl.updateValueAndValidity();
    }

    this.detailsGroup.markAllAsTouched();
    if (this.detailsGroup.valid) {
      this.bookingButtonValue = this.translocoService.translate('SUMMARY.LOADING');;
      this.addOrder();
    }
  }

  private isBookingAlreadyExists(val: any): boolean {
    if (!val) return false;
    let str = '';
    if (typeof val === 'string') {
      str = val;
    } else if (typeof val === 'object') {
      if (typeof val.error === 'string') {
        str = val.error;
      } else if (typeof val.error?.message === 'string') {
        str = val.error.message;
      } else if (typeof val.message === 'string') {
        str = val.message;
      } else if (typeof val.comment === 'string') {
        str = val.comment;
      } else {
        try {
          str = JSON.stringify(val);
        } catch {
          str = '';
        }
      }
    }
    const lower = str.toLowerCase();
    return lower.includes('already exists') || lower.includes('pre-booked');
  }

  addOrder() {
    const customerData = this._form.createCustomerData();
    this.isLoading = true;

    this._shuttleBussService
      .addOrder(customerData)
      .pipe(
        timeout(30000),
        catchError(error => {
          this.isLoading = false;
          this._form._toThePayment.next(false);

          if (!navigator.onLine) {
            console.error('No internet connection detected.');
            this.snackBar.open(
              'Ingen internetanslutning. Kontrollera din nätverksanslutning.',
              'OK',
              {
                duration: 5000,
                verticalPosition: 'top',
                horizontalPosition: 'center'
              }
            );
          } else if (error.name === 'TimeoutError') {
            console.error('API request timed out after 30 seconds.');
            this.snackBar.open(
              'Betalningsförfrågan tog för lång tid. Vänligen försök igen.',
              'OK',
              {
                duration: 5000,
                verticalPosition: 'top',
                horizontalPosition: 'center'
              }
            );
          } else if (this.isBookingAlreadyExists(error)) {
            this.detailsComponent?.openBookingAlreadyExistsModal();
            return of(null);
          } else {
            console.error('API error:', error);
            this.snackBar.open(
              'Något gick fel! Vänligen försök igen.',
              'OK',
              {
                duration: 5000,
                verticalPosition: 'top',
                horizontalPosition: 'center'
              }
            );
          }
          return throwError(() => error);
        })
      )
      .subscribe({
        next: (cart: any) => {
          if (!cart) return;
          if (this.isBookingAlreadyExists(cart)) {
            this.isLoading = false;
            this._form._toThePayment.next(false);
            this.detailsComponent?.openBookingAlreadyExistsModal();
            return;
          }
          sessionStorage.removeItem('isFromCustomerHome');
          sessionStorage.removeItem('selectedParkingFromSearch');
          if (cart?.comment?.toLowerCase() == 'driveup') {
            this._router.navigate(['parkifly', this.site, 'checkout', 'payment', cart?.orderId]);
            return;
          }
          this.orderId = cart.orderId;
          this._form.paymentReference = null;
          this.cartService.setOrderId(cart.orderId);
          this.createCartOrder();
          if (cart) {
            this.isLoading = false;
            this._form._toThePayment.next(false);
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.error('error', error);
          this._form._toThePayment.next(false);
        },
        complete: () => {
        }
      })
  }

  public createCartOrder(): void {
    this._shuttleBussService.createCartOrder(this.orderId).subscribe({
      next: (payment) => {
        if (payment?.status == 'Paid') {
          this._router.navigate(['parkifly', this.site, 'checkout', 'receipt', payment.orderId, 'NONE']);
        }
        this._form.paymentReference = payment;
        const control = this.paymentGroup.get('goToNextStep');
        control?.setErrors(null);
        control?.setValue(true);
        this.stepper.next();
      },
      error: (err) => {
        console.error('Error occurred while creating cart order:', err);
      }
    });
  }

  cancelOrder() {
    this.cartService.abortorder(this.orderId).subscribe({
      next: (result) => {
        if (result == true) {
          this.orderId = null;
          this._form.paymentReference = null;
          this.cartService.setOrderId(null);
        }
      },
      error: (err) => {
        console.error('Error occurred while fetching order details:', err);
      },
      complete: () => {
      }
    });
  }

  next(): void {
    this.isParkingSelected = true;
    this.maintenanceService.checkMaintenanceTime();

    this._form.getForm().get('parking').controls.selectedParking.valueChanges.subscribe((parking: any) => {
      if (parking) {
        this.isadditionalServicesEnabled = parking.additionalServicesEnabled ?? false;
      } else {
        this.isadditionalServicesEnabled = false;
      }
    });

    setTimeout(() => {
      let additionalServices = this._form.selectedParking?.additionalServices ?? null;
      if (
        Array.isArray(additionalServices) &&
        additionalServices.length == 0 &&
        this.isadditionalServicesEnabled
      ) {
        this.stepper.selectedIndex = 2;
      } else {
        this.stepper.next();
      }
    }, 0);
  }

  onStepChange(event: StepperSelectionEvent) {
    this.maintenanceService.checkMaintenanceTime();

    if ((event.selectedIndex == (this.stepper.steps.length - 1)) && (this._form.paymentReference == undefined || this._form.paymentReference == null)) {
      this.stepper.selectedIndex = event.previouslySelectedIndex;
      return;
    }

    if (event.selectedIndex == 1 && event.previouslySelectedIndex == 2) {
      this._form.toThePayment = false;
    }
  }

  onSelectedIndexChange(index: number) {
    const wasOnPaymentStep = this.lastStepIndex === (this.stepper.steps.length - 1);
    const goingBack = index < this.lastStepIndex;

    if (wasOnPaymentStep && goingBack) {
      this.cancelOrder();

      const control = this.paymentGroup.get('goToNextStep');
      control?.setValue(null);
      control?.setErrors({ required: true });
      control?.markAsUntouched();
      control?.markAsPristine();

      this.paymentGroup.reset();
      this.isPaymentStepActive = false;
    }

    this.lastStepIndex = index;
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

}